import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    TableCell,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import PeopleIcon from '@material-ui/icons/People'
import { toast } from 'react-toastify'

import { Container } from '../../../components'
import { axiosInstance } from '../../../helpers'
import { Address, BaseCompany, IdBoolMap } from '../../../models'
import { SearchField } from '../../../components/SearchField'
import {
    CellData,
    HeadCell,
    RowData,
    SimpleTable,
} from '../../../components/Table'
import { SelectedWorkspacePanel } from '../../../components/AdminPanel/WorkspaceModal/SelectedWorkspacePanel'
import { useStyles } from '../../../styles'
import { typographyStyle } from '../../../theme/typographyStyle'

interface CompanyWithInvoiceCode extends BaseCompany {
    invoice_code: string | null
}

interface CompanyRow extends RowData {
    id: CellData<number>
    organization: CellData<string>
    type: CellData<string>
    name: CellData<string>
    address: CellData<Address | null>
    invoice_code: CellData<string | null>
    company: CellData<CompanyWithInvoiceCode>
}

type TableState = {
    isLoading: boolean
    serverRes: CompanyWithInvoiceCode[]
}

const mapCompanyToRow = (company: CompanyWithInvoiceCode): CompanyRow => {
    const companyTypeStr =
        company.company_type === 'APARTMENT' ? 'Apartment' : 'Vendor'
    return {
        id: { value: company.id, sortValue: company.id },
        name: { value: company.name, sortValue: company.name },
        organization: {
            value: company.organization.name,
            sortValue: company.organization.name,
        },
        address: {
            value: company.primary_address,
            sortValue: company.primary_address?.state ?? '',
        },
        type: { value: companyTypeStr, sortValue: companyTypeStr },
        invoice_code: {
            value: company.invoice_code,
            sortValue: company.invoice_code ?? '',
        },
        company: { value: company, sortValue: company.name },
    }
}

export const WorkspaceManagerPage = () => {
    const theme = useTheme()
    const classes = useStyles()

    const [searchText, setSearchText] = useState('')
    const [userEmailSearch, setUserEmailSearch] = useState('')
    const [tableState, setTableState] = useState<TableState>({
        isLoading: false,
        serverRes: [],
    })
    const [
        selectedWorkspace,
        setSelectedWorkspace,
    ] = useState<BaseCompany | null>(null)
    const [
        selectedWorkspacesMap,
        setSelectedWorkspacesMap,
    ] = useState<IdBoolMap>({})
    const [dialogOpen, setDialogOpen] = useState(false)

    useEffect(() => {
        makeRequest()
    }, [])

    const makeRequest = (emailFilter?: string) => {
        setTableState((prev) => ({ ...prev, isLoading: true }))
        axiosInstance
            .get('company/admin-company-manage/', {
                params: emailFilter
                    ? { user_email: emailFilter, simple_serializer: true }
                    : { simple_serializer: true },
            })
            .then((res) => {
                const data = res.data
                setTableState({
                    serverRes: Array.isArray(data) ? data : data.results ?? [],
                    isLoading: false,
                })
            })
            .catch(() => setTableState({ serverRes: [], isLoading: false }))
    }

    const selectedWorkspaceIds = Object.keys(selectedWorkspacesMap).reduce<
        number[]
    >((prev, id) => {
        const numId = Number(id)
        return selectedWorkspacesMap[numId] ? prev.concat(numId) : prev
    }, [])

    const filteredRows = useMemo(
        () =>
            tableState.serverRes
                .filter((row) =>
                    row.name
                        .toLocaleLowerCase()
                        .includes(searchText.toLocaleLowerCase()),
                )
                .map(mapCompanyToRow),
        [tableState.serverRes, searchText],
    )

    const headCellStyle: React.CSSProperties = {
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        ...typographyStyle(theme.typography.h6),
        flex: 2,
        justifyContent: 'center',
    }

    const headCells: HeadCell<RowData>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'check',
            label: '',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'id',
            label: 'ID',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'type',
            label: 'Type',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'organization',
            label: 'Organization',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'name',
            label: 'Name',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'invoice_code',
            label: 'Invoice Code',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'address',
            label: 'Address',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'actions',
            label: '',
            style: headCellStyle,
        },
    ]

    const grey100 = theme.palette.grey[100]
    const grey300 = theme.palette.grey[300]
    const primary = theme.palette.primary.main

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[200],
            }}
            scrollY
        >
            {/* ── Page header ── */}
            <Paper
                elevation={0}
                square
                style={{
                    padding: theme.spacing(3, 4),
                    borderBottom: `1px solid ${grey300}`,
                    backgroundColor: '#fff',
                }}
            >
                <Typography
                    variant="h5"
                    style={{
                        fontWeight: 700,
                        color: theme.palette.darkBlue.main,
                    }}
                >
                    Workspace Manager
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        marginTop: theme.spacing(0.5),
                        color: theme.palette.text.secondary,
                    }}
                >
                    Search, inspect, and manage company workspaces. Bulk-add
                    users across multiple workspaces at once.
                </Typography>
            </Paper>

            {/* ── Toolbar ── */}
            <Paper
                elevation={0}
                square
                style={{
                    padding: theme.spacing(1.5, 3),
                    borderBottom: `1px solid ${grey300}`,
                    backgroundColor: '#fff',
                }}
            >
                <Container style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <SearchField
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 280, marginRight: theme.spacing(2) }}
                        placeholder="Search by company name"
                    />

                    <SearchField
                        value={userEmailSearch}
                        onChange={(e) => setUserEmailSearch(e.target.value)}
                        style={{ width: 280, marginRight: theme.spacing(1) }}
                        placeholder="Filter by user email"
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        style={{
                            marginRight: theme.spacing(1),
                            textTransform: 'none',
                        }}
                        onClick={() => makeRequest(userEmailSearch)}
                    >
                        Apply
                    </Button>

                    <Button
                        variant="outlined"
                        style={{
                            marginRight: theme.spacing(2),
                            textTransform: 'none',
                        }}
                        onClick={() => {
                            setUserEmailSearch('')
                            makeRequest()
                        }}
                    >
                        Clear
                    </Button>

                    <div style={{ flex: 1 }} />

                    {selectedWorkspaceIds.length > 0 && (
                        <Chip
                            label={`${selectedWorkspaceIds.length} selected`}
                            size="small"
                            style={{
                                backgroundColor: primary,
                                color: '#fff',
                                fontWeight: 700,
                                marginRight: theme.spacing(1),
                            }}
                        />
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedWorkspaceIds.length === 0}
                        style={{
                            marginRight: theme.spacing(1),
                            textTransform: 'none',
                        }}
                        onClick={() => setDialogOpen(true)}
                    >
                        <PeopleIcon style={{ fontSize: 18, marginRight: 6 }} />
                        Bulk Add User
                    </Button>

                    <Button
                        variant="outlined"
                        disabled={selectedWorkspaceIds.length === 0}
                        style={{ textTransform: 'none' }}
                        onClick={() => setSelectedWorkspacesMap({})}
                    >
                        Deselect All
                    </Button>
                </Container>
            </Paper>

            {/* ── Body ── */}
            <Container
                style={{
                    flex: 1,
                    padding: theme.spacing(3),
                    alignItems: 'flex-start',
                    overflow: 'auto',
                }}
            >
                {/* Table panel */}
                <Paper
                    variant="outlined"
                    style={{
                        flex: 1,
                        borderRadius: 10,
                        backgroundColor: '#fff',
                        overflow: 'hidden',
                        minWidth: 0,
                    }}
                >
                    {tableState.isLoading ? (
                        <Container
                            style={{
                                justifyContent: 'center',
                                padding: theme.spacing(6),
                            }}
                        >
                            <CircularProgress />
                        </Container>
                    ) : (
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                            <SimpleTable<CompanyRow>
                                rows={filteredRows}
                                orderByDefault="id"
                                headCells={headCells}
                                render={(row) => {
                                    const address = row.address.value
                                    const isChecked =
                                        selectedWorkspacesMap[row.id.value] ===
                                        true
                                    return (
                                        <TableRow
                                            key={`WS_ROW_${row.id.value}`}
                                            className={classes.hoverGrey200}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isChecked}
                                                    style={{ color: primary }}
                                                    onChange={() =>
                                                        setSelectedWorkspacesMap(
                                                            {
                                                                ...selectedWorkspacesMap,
                                                                [row.id
                                                                    .value]: !isChecked,
                                                            },
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {row.id.value}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.type.value}
                                                    size="small"
                                                    style={{
                                                        backgroundColor:
                                                            row.type.value ===
                                                            'Apartment'
                                                                ? primary + '22'
                                                                : theme.palette
                                                                      .secondary
                                                                      .main +
                                                                  '22',
                                                        color:
                                                            row.type.value ===
                                                            'Apartment'
                                                                ? primary
                                                                : theme.palette
                                                                      .secondary
                                                                      .main,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {row.organization.value}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {row.name.value}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {row.invoice_code.value ??
                                                        '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {address === null ? (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        N/A
                                                    </Typography>
                                                ) : (
                                                    <div>
                                                        <Typography variant="body2">
                                                            {address?.address1}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {address.city},{' '}
                                                            {address.state}{' '}
                                                            {address.zip_code}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setSelectedWorkspace(
                                                            row.company.value,
                                                        )
                                                    }
                                                >
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }}
                            />
                        </div>
                    )}
                </Paper>

                {/* Detail panel */}
                {selectedWorkspace !== null && (
                    <Paper
                        variant="outlined"
                        style={{
                            width: 600,
                            flexShrink: 0,
                            marginLeft: theme.spacing(3),
                            borderRadius: 10,
                            overflow: 'hidden',
                            backgroundColor: '#fff',
                            position: 'sticky',
                            top: 0,
                        }}
                    >
                        <SelectedWorkspacePanel
                            theme={theme}
                            workspaceDetail={selectedWorkspace}
                            updateWorkspaceAddress={(address) => {
                                setSelectedWorkspace({
                                    ...selectedWorkspace,
                                    primary_address: address,
                                })
                                setTableState((prev) => ({
                                    ...prev,
                                    serverRes: prev.serverRes.map((row) =>
                                        row.id === selectedWorkspace.id
                                            ? {
                                                  ...row,
                                                  primary_address: address,
                                              }
                                            : row,
                                    ),
                                }))
                            }}
                            handleClose={() => setSelectedWorkspace(null)}
                        />
                    </Paper>
                )}
            </Container>

            <BulkAddUserDialog
                open={dialogOpen}
                workspaceCount={selectedWorkspaceIds.length}
                onClose={() => setDialogOpen(false)}
                onConfirm={(email) => {
                    axiosInstance
                        .post('user/_admin/', {
                            email,
                            workspaces: selectedWorkspaceIds,
                        })
                        .then(() =>
                            toast.success(
                                `${email} added to ${selectedWorkspaceIds.length} workspaces`,
                            ),
                        )
                        .catch((e) =>
                            toast.error(`Error: ${e.response.data}`, {
                                autoClose: 5000,
                            }),
                        )
                }}
            />
        </Container>
    )
}

interface BulkAddUserDialogProps {
    open: boolean
    workspaceCount: number
    onClose: () => void
    onConfirm: (email: string) => void
}

const BulkAddUserDialog = ({
    open,
    workspaceCount,
    onClose,
    onConfirm,
}: BulkAddUserDialogProps) => {
    const [email, setEmail] = useState('')

    const handleClose = () => {
        setEmail('')
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                Add user to {workspaceCount} workspace
                {workspaceCount !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Enter the email address to add.
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && email) {
                            onConfirm(email)
                            handleClose()
                        }
                    }}
                    style={{ marginTop: 8 }}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!email}
                    onClick={() => {
                        onConfirm(email)
                        handleClose()
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}
